import React from 'react';

import {
  CircularProgress,
} from '@mui/material';
import imageCompression from 'browser-image-compression';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

import Button from '../../../components/Button';
import ContentCard from '../../../components/ContentCard';
import EditField from '../../../components/EditField';
import ProfileHeader from '../../../components/ProfileHeader';
import { ColorHex } from '../../../components/ProfileIcon/types';
import SkeletonStack from '../../../components/SkeletonStack';
import { getCurrentUserId, signOut } from '../../../services/auth';
import { putImage, s3ToCloudfrontUrl } from '../../../services/aws';
import {
  useGetCurrentUserQuery, useLazyGetProfileImageUrlQuery, useUpdateUserMutation,
} from '../../../services/users';
import { UserGroup } from '../../../types/common';
import { formatPhoneNumber } from '../../../utils/common/formatText';
import { formatUserGroups } from '../../../utils/common/formatUserGroups';
import useError from '../../../utils/react/hooks/useError';
import useUserGroups from '../../../utils/react/hooks/useUserGroups';

import {
  DetailsSection,
  FieldContainer,
  FieldName,
  FieldRow,
  LogoutIcon,
  StyledBackdrop,
  TabContainer,
} from './index.styles';

const ProfilePage = () => {
  const userGroups = useUserGroups();

  // Get user details
  const userId = getCurrentUserId();
  const { data: currUser, isLoading } = useGetCurrentUserQuery();

  const [updateUser, { isLoading: isUpdateUserLoading,
    isError: isUpdateUserError,
    error: updateUserError }] = useUpdateUserMutation();
  const [getUrl, { isError: isGetUrlError, error: getUrlError }] = useLazyGetProfileImageUrlQuery();

  const handleIconUpdate = async (image?: File, color?: ColorHex) => {
    if (userId) {
      // If an image is to be uploaded
      if (image) {
        // Gets the url to upload the user profile image
        const url = (await getUrl({ userId: userId || '' })).data?.data;

        if (!url) return;

        try {
          // compresses the image
          const options = {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 500,
            useWebWorker: true,
          };

          const compressedImage = await imageCompression(image, options);

          // Puts the image to S3
          await putImage(url, compressedImage);

          // Splits the part of the url for getting the actual image
          // Appends current time in ms to create unique image URL to force frontend refresh
          const imageUrl = `${s3ToCloudfrontUrl(url)}?v=${dayjs().valueOf()}`;

          updateUser({
            userId,
            newAttributes: { profileIcon: { image: imageUrl, color: '' } },
          });
        } catch (error) {
          useError(true, 'Something went wrong. Please try again.');
        }
      } else if (color) {
        updateUser({
          userId,
          newAttributes: { profileIcon: { image: '', color } },
        });
      }
    }
  };

  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    await navigate('/signin');
  };

  useError(isUpdateUserError, updateUserError);
  useError(isGetUrlError, getUrlError);

  return (
    <>
      {isUpdateUserLoading && (
        <StyledBackdrop open>
          <CircularProgress />
        </StyledBackdrop>
      )}
      {
        (isLoading || !currUser?.data) ? <SkeletonStack /> : (
          <TabContainer>
            <ContentCard>
              <ProfileHeader
                name={currUser?.data.name}
                description={formatUserGroups(
                  Object.values(UserGroup).filter((x) => userGroups.includes(x)),
                )}
                profileIconProps={{
                  firstName: currUser?.data?.firstName,
                  lastName: currUser?.data?.lastName,
                  color: currUser?.data?.profileIcon?.color,
                  imageUrl: currUser?.data?.profileIcon?.image,
                }}
                onIconEdit={handleIconUpdate}
              />
            </ContentCard>
            {/* {userGroups.includes('Students') && (
              <ContentCard title="Unique Referral Code">
                <DiscountDescription variant="body2">
                  Know someone who would enjoy studying at Cognito as much as you? Have them use
                  your referral code when they enrol at {' '}
                  <ContrastLink
                    href="https://cognitotuition.com/enrol"
                    target="_blank"
                    rel="noreferrer"
                  >cognitotuition.com/enrol
                  </ContrastLink>!
                </DiscountDescription>
                <ReferralCode variant="h5">
                  {getReferralCode(userData?.firstName?.value, userData?.phoneNumber?.value)}
                </ReferralCode>
                <DiscountDescription variant="body2">
                  Your friend will get <b>$100 off of their first invoice</b>. If they
                  continue beyond their 2-week trial, you can claim a <b>$50 gift card</b> from
                  the Cognito reception desk! Yes, a gift card for each student you refer!
                </DiscountDescription>
                <DiscountTerms>
                  <Typography variant="caption">
                    <b>Terms and Conditions</b><br />
                    1. You must be a currently enrolled, non-trial student at Cognito Tuition.<br />
                    2. The code is only valid for 5 uses before the 30th of September 2023.<br />
                    3. Only prospective students who have not previously enrolled at Cognito
                    Tuition are eligible for referral.<br />
                    4. Discounts are not redeemable for cash and can not be claimed in
                    conjunction with other promotional offers.<br />
                  </Typography>
                </DiscountTerms>
              </ContentCard>
            )} */}
            <ContentCard title="Profile Details">
              <DetailsSection>
                <FieldRow>
                  <FieldName variant="h6">Email</FieldName>
                  <FieldContainer>
                    <EditField
                      type="text"
                      value={currUser?.data.email}
                      disabled
                    />
                  </FieldContainer>
                </FieldRow>
                <FieldRow>
                  <FieldName variant="h6">Phone</FieldName>
                  <FieldContainer>
                    <EditField
                      type="text"
                      value={formatPhoneNumber(currUser?.data.phoneNumber)}
                      disabled
                    />
                  </FieldContainer>
                </FieldRow>
              </DetailsSection>
            </ContentCard>
            <ContentCard>
              <Button onClick={handleSignOut}><LogoutIcon />Sign Out</Button>
            </ContentCard>
          </TabContainer>
        )
      }
    </>
  );
};
export default ProfilePage;
